import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import type { FieldArrayWithId } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import type { PriorityFormValues } from "./priority-form-schema";
import { AssigneeLoadBadge } from "./AssigneeLoadBadge";
import { AssigneeBlockerFields } from "./AssigneeBlockerFields";
import { AssigneeRowActions } from "./AssigneeRowActions";
import {
    ASSIGNEE_STATUS_OPTIONS,
    AssigneeLoad,
    avatarAssigneeClass,
    avatarAssigneeFallbackClass,
    DisplayUser,
    isAssigneeStatusDisabled,
    sectionKickerClass,
} from "./priority-form-constants";

interface AssigneeRowProps {
    row: FieldArrayWithId<PriorityFormValues, "assignments", "id">;
    index: number;
    isEdit: boolean;
    isDri: boolean;
    /**
     * When true, roster controls (Set DRI, remove) lock: they are task-level, owned by the
     * DRI/creator/admin. Needed on top of the enclosing row fieldset because the current user's own row
     * stays enabled for status/IC-task edits.
     */
    rosterLocked?: boolean;
    /**
     * When true, every field on this row locks (another assignee's row on a DRI-locked form). The
     * enclosing fieldset already disables the native controls; this prop reaches the Radix widgets
     * (Select, Popover), whose pointerdown-driven dropdowns a disabled fieldset does not suppress.
     */
    rowLocked?: boolean;
    isCurrentUser?: boolean;
    assigneeNameByPid: Map<string, string>;
    userByPid: Map<string, DisplayUser>;
    /** This user's Focus/Queue load across other priorities, for inline limit prevention. */
    load?: AssigneeLoad;
    /** Blocker from server when editing, for label before search loads */
    initialAssigneeBlocker?: { pid: string; fullName: string };
    onRemove: (pid: string) => void;
    onSetDri: (pid: string) => void;
}

export function AssigneeRow({
    row,
    index,
    isEdit,
    isDri,
    rosterLocked = false,
    rowLocked = false,
    isCurrentUser = false,
    assigneeNameByPid,
    userByPid,
    load,
    initialAssigneeBlocker,
    onRemove,
    onSetDri,
}: AssigneeRowProps) {
    const form = useFormContext<PriorityFormValues>();
    const name = assigneeNameByPid.get(row.userPid) ?? row.userPid;
    const u = userByPid.get(row.userPid);
    const first = name.split(/\s+/)[0] ?? name;

    const assigneeStatus = useWatch({
        control: form.control,
        name: `assignments.${index}.status`,
    });

    return (
        <div className={cn("rounded-lg border bg-muted/30 p-2.5", isDri ? "border-primary/40 bg-primary/[0.03]" : "border-border")}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <UserAvatar
                    src={u?.avatarUrl}
                    name={name}
                    className={avatarAssigneeClass}
                    fallbackClassName={avatarAssigneeFallbackClass}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{name}</span>
                <AssigneeLoadBadge load={load} className="shrink-0" />
                {u?.position && (
                    <span className="hidden shrink-0 text-[10px] text-muted-foreground sm:inline">{u.position}</span>
                )}
                <AssigneeRowActions
                    userPid={row.userPid}
                    name={name}
                    isDri={isDri}
                    rosterLocked={rosterLocked}
                    onSetDri={onSetDri}
                    onRemove={onRemove}
                />
            </div>

            {isEdit ? (
                <>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <FormField
                            control={form.control}
                            name={`assignments.${index}.status`}
                            render={({ field: st }) => (
                                <FormItem>
                                    <FormLabel className="text-[10px] uppercase text-muted-foreground">
                                        Assignee status
                                    </FormLabel>
                                    <Select onValueChange={st.onChange} value={st.value} disabled={rowLocked}>
                                        <FormControl>
                                            <SelectTrigger className="h-9 bg-background text-xs">
                                                <SelectValue placeholder="Status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {ASSIGNEE_STATUS_OPTIONS.map((option) => {
                                                const disabled = isAssigneeStatusDisabled(option.value, load, st.value);
                                                return (
                                                    <SelectItem
                                                        key={option.value}
                                                        value={option.value}
                                                        disabled={disabled}
                                                        title={
                                                            disabled
                                                                ? `${first} is already at the ${option.label} limit (${option.value === "focus" ? "1 Focus" : "2 Queue"}). Change their other tasks first.`
                                                                : option.description
                                                        }
                                                    >
                                                        {option.label}
                                                        {disabled ? " — full" : ""}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name={`assignments.${index}.icTask`}
                            render={({ field: fa }) => (
                                <FormItem>
                                    <FormLabel className="text-[10px] uppercase text-muted-foreground">
                                        {isCurrentUser ? "IC Task *" : "IC Task"}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={`What will ${first} work on?`}
                                            className="h-9 text-xs"
                                            {...fa}
                                            value={fa.value ?? ""}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    {assigneeStatus === "blocked" ? (
                        <AssigneeBlockerFields
                            index={index}
                            rowLocked={rowLocked}
                            rowUserPid={row.userPid}
                            initialAssigneeBlocker={initialAssigneeBlocker}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    <p className={cn(sectionKickerClass, "mb-1 normal-case tracking-normal")}>{isCurrentUser ? "IC Task *" : "IC Task"}</p>
                    <FormField
                        control={form.control}
                        name={`assignments.${index}.icTask`}
                        render={({ field: fa }) => (
                            <FormItem className="space-y-0">
                                <FormControl>
                                    <Input
                                        placeholder={`What will ${first} work on?`}
                                        className="h-8 text-xs"
                                        {...fa}
                                        value={fa.value ?? ""}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </>
            )}
        </div>
    );
}
